/**
 * WhatsNewModal — full reverse-chronological release history.
 *
 * Triggered by the "View all past changes" link on WhatsNewCard, OR by
 * any Settings/Help affordance the host adds later. Read-only — just
 * renders every WHATS_NEW entry. Easy to add a "subscribe to release
 * notifications" hook here if we ever want one.
 */

import { X, Sparkles } from 'lucide-react';
import { WHATS_NEW, SEVERITY_COLOR } from '../../utils/whatsNew';

const SeverityDot = ({ severity = 'feature' }) => {
  const token = SEVERITY_COLOR[severity] || SEVERITY_COLOR.feature;
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 mr-2 shrink-0 ${token.dot}`}
      title={token.label}
      aria-label={token.label}
    />
  );
};

const WhatsNewModal = ({ open, onClose, darkMode = true }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Release history"
    >
      <div
        className={`relative rounded-xl shadow-2xl border max-w-2xl w-full max-h-[80vh] flex flex-col ${
          darkMode
            ? 'bg-gradient-to-br from-purple-950/95 via-gray-900/95 to-gray-900/95 border-purple-500/40'
            : 'bg-gradient-to-br from-purple-50 via-white to-white border-purple-300'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center gap-2 px-5 py-3 border-b ${
          darkMode ? 'border-purple-500/20 bg-purple-500/10' : 'border-purple-200 bg-purple-50/60'
        }`}>
          <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
            darkMode ? 'bg-purple-500/30 text-purple-200' : 'bg-purple-200 text-purple-700'
          }`}>
            <Sparkles size={14} />
          </div>
          <div className="flex-1">
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-purple-100' : 'text-purple-900'}`}>
              Release history
            </h2>
            <p className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {WHATS_NEW.length} release{WHATS_NEW.length === 1 ? '' : 's'} tracked
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`p-1.5 rounded-md transition-colors ${
              darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — every entry, newest first */}
        <div className="px-5 py-4 space-y-5 overflow-y-auto flex-1">
          {WHATS_NEW.map((entry) => (
            <div key={entry.version} className="space-y-2">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`text-sm font-semibold ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                  v{entry.version}
                </span>
                <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {entry.date}
                </span>
              </div>
              {entry.title && (
                <p className={`text-[12px] italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {entry.title}
                </p>
              )}
              <ul className="space-y-1.5">
                {(entry.highlights || []).map((h, i) => (
                  <li key={i} className="flex items-start gap-0">
                    <SeverityDot severity={h.severity} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] leading-snug ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {h.text}
                      </p>
                      {h.action && (
                        <p className={`text-[10px] italic mt-0.5 leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          → {h.action}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-2 px-5 py-3 border-t ${
          darkMode ? 'border-purple-500/15 bg-black/20' : 'border-purple-200 bg-purple-50/40'
        }`}>
          <button
            type="button"
            onClick={onClose}
            className={`text-[11px] font-semibold px-3 py-1.5 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-purple-500/25 border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/25 hover:bg-purple-500/35 hover:border-purple-400/70 hover:shadow-[0_0_14px_rgba(168,85,247,0.25)]'
                : 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
